import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
  useRef,
} from "react";
import * as signalR from "@microsoft/signalr";
import { useSelector } from "react-redux";

/**
 * SignalR Context for real-time POS updates
 * Manages connection to /hubs/devices hub
 */

const SignalRContext = createContext(null);

export const useSignalR = () => {
  const context = useContext(SignalRContext);
  if (!context) {
    throw new Error("useSignalR must be used within SignalRProvider");
  }
  return context;
};

export const SignalRProvider = ({ children }) => {
  const [connection, setConnection] = useState(null);
  const [connectionState, setConnectionState] = useState("Disconnected");
  const [error, setError] = useState(null);
  const token = useSelector((state) => state.auth.token);
  const storeId = useSelector((state) => state.auth.user?.storeId);
  const isStartingRef = useRef(false);
  const connectionRef = useRef(null);
  const connectionStateRef = useRef("Disconnected");
  const listenersRef = useRef(new Map());
  const lastSyncAtRef = useRef(new Date().toISOString());
  const sessionRef = useRef(null);

  const baseUrl = (
    process.env.REACT_APP_API_BASE_URL || "http://localhost:50268"
  ).replace(/\/+$/, "");
  const fallbackHubUrl = `${baseUrl}/hubs/devices`;

  const getRealtimeHeaders = useCallback(
    (extra = {}) => {
      const deviceToken = localStorage.getItem("edgeDeviceToken");
      return {
        "Content-Type": "application/json",
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        ...(deviceToken ? { "X-DEVICE-TOKEN": deviceToken } : {}),
        ...extra,
      };
    },
    [token],
  );

  const emitRealtimeEvent = useCallback((eventName, payload) => {
    const callbacks = listenersRef.current.get(eventName);
    if (!callbacks) return;
    callbacks.forEach((callback) => {
      try {
        callback(payload);
      } catch (err) {
        console.error(`[SignalR] Listener error for ${eventName}:`, err);
      }
    });
  }, []);

  const syncRealtimeDelta = useCallback(
    async (since) => {
      try {
        const params = new URLSearchParams();
        if (since) params.append("since", since);
        params.append("scope", "STORE");

        const response = await fetch(
          `${baseUrl}/api/edge/realtime/sync?${params.toString()}`,
          {
            method: "GET",
            headers: getRealtimeHeaders(),
          },
        );

        if (!response.ok) {
          return;
        }

        const data = await response.json();
        const events = Array.isArray(data?.events) ? data.events : [];

        events.forEach((event) => {
          const eventType = event?.eventType;
          const payload = event?.payload || event;
          if (eventType) {
            emitRealtimeEvent(eventType, payload);
          }
        });

        lastSyncAtRef.current = data?.serverTime || new Date().toISOString();
      } catch (err) {
        console.warn("[SignalR] Delta sync failed:", err);
      }
    },
    [baseUrl, getRealtimeHeaders, emitRealtimeEvent],
  );

  const createRealtimeSession = useCallback(async () => {
    try {
      const deviceToken = localStorage.getItem("edgeDeviceToken");
      const response = await fetch(`${baseUrl}/api/edge/realtime/session`, {
        method: "POST",
        headers: getRealtimeHeaders(),
        body: JSON.stringify({
          clientType: "POS",
          deviceToken: deviceToken || undefined,
          subscribe: ["STORE", "DEVICE", "ORDER"],
          orderIds: [],
        }),
      });

      if (!response.ok) {
        return null;
      }

      return await response.json();
    } catch (err) {
      console.warn("[SignalR] Realtime session fallback:", err);
      return null;
    }
  }, [baseUrl, getRealtimeHeaders]);

  // Create connection
  const createConnection = useCallback(
    ({ targetHubUrl, accessToken, deviceToken }) => {
    if (!token) return null;

    const newConnection = new signalR.HubConnectionBuilder()
      .withUrl(targetHubUrl, {
        accessTokenFactory: () => deviceToken || accessToken || "",
        transport:
          signalR.HttpTransportType.WebSockets |
          signalR.HttpTransportType.ServerSentEvents,
      })
      .withAutomaticReconnect({
        nextRetryDelayInMilliseconds: (retryContext) => {
          // Exponential backoff: 0s, 2s, 10s, 30s, then 30s
          if (retryContext.previousRetryCount === 0) return 0;
          if (retryContext.previousRetryCount === 1) return 2000;
          if (retryContext.previousRetryCount === 2) return 10000;
          return 30000;
        },
      })
      .configureLogging(signalR.LogLevel.Information)
      .build();

    // Connection state handlers
    newConnection.onreconnecting((error) => {
      setConnectionState("Reconnecting");
      setError(error?.message || "Reconnecting...");
      console.log("[SignalR] Reconnecting...", error);
    });

    newConnection.onreconnected((connectionId) => {
      setConnectionState("Connected");
      setError(null);
      console.log("[SignalR] Reconnected:", connectionId);

      syncRealtimeDelta(lastSyncAtRef.current);
    });

    newConnection.onclose((error) => {
      setConnectionState("Disconnected");
      if (error) {
        setError(error.message);
        console.error("[SignalR] Connection closed with error:", error);
      } else {
        console.log("[SignalR] Connection closed");
      }
    });

    return newConnection;
    },
    [token, storeId, syncRealtimeDelta],
  );

  // Start connection
  const startConnection = useCallback(async () => {
    if (!token || !storeId) {
      console.log("[SignalR] Missing token or storeId, skipping connection");
      return;
    }

    const existingConnection = connectionRef.current;
    const currentState = connectionStateRef.current;
    if (
      existingConnection &&
      (currentState === "Connecting" ||
        currentState === "Connected" ||
        currentState === "Reconnecting")
    ) {
      return;
    }

    if (isStartingRef.current) {
      return;
    }
    isStartingRef.current = true;

    try {
      const realtimeSession = await createRealtimeSession();
      sessionRef.current = realtimeSession;

      const sessionHubUrl = realtimeSession?.hubUrl
        ? realtimeSession.hubUrl.startsWith("http")
          ? realtimeSession.hubUrl
          : `${baseUrl}${realtimeSession.hubUrl}`
        : fallbackHubUrl;
      const deviceToken = localStorage.getItem("edgeDeviceToken");
      const newConnection = createConnection({
        targetHubUrl: sessionHubUrl,
        accessToken: realtimeSession?.accessToken || token,
        deviceToken: deviceToken || undefined,
      });
      if (!newConnection) return;

      setConnectionState("Connecting");
      setError(null);

      await newConnection.start();
      connectionRef.current = newConnection;
      setConnection(newConnection);
      setConnectionState("Connected");
      console.log("[SignalR] Connected successfully");

      await syncRealtimeDelta(lastSyncAtRef.current);

      // Re-register all existing listeners
      listenersRef.current.forEach((callbacks, eventName) => {
        callbacks.forEach((callback) => {
          newConnection.on(eventName, callback);
        });
      });
    } catch (err) {
      setConnectionState("Disconnected");
      setError(err.message);
      console.error("[SignalR] Connection failed:", err);
    } finally {
      isStartingRef.current = false;
    }
  }, [
    token,
    storeId,
    baseUrl,
    fallbackHubUrl,
    createConnection,
    createRealtimeSession,
    syncRealtimeDelta,
  ]);

  // Stop connection
  const stopConnection = useCallback(async () => {
    isStartingRef.current = false;

    const activeConnection = connectionRef.current;
    if (activeConnection) {
      try {
        await activeConnection.stop();
        console.log("[SignalR] Connection stopped");
      } catch (err) {
        console.error("[SignalR] Error stopping connection:", err);
      }
      connectionRef.current = null;
      sessionRef.current = null;
      setConnection(null);
      setConnectionState("Disconnected");
    }
  }, []);

  useEffect(() => {
    connectionStateRef.current = connectionState;
  }, [connectionState]);

  // Subscribe to event
  const on = useCallback(
    (eventName, callback) => {
      if (!listenersRef.current.has(eventName)) {
        listenersRef.current.set(eventName, new Set());
      }
      listenersRef.current.get(eventName).add(callback);

      if (connection && connectionState === "Connected") {
        connection.on(eventName, callback);
      }

      // Return unsubscribe function
      return () => {
        const callbacks = listenersRef.current.get(eventName);
        if (callbacks) {
          callbacks.delete(callback);
          if (callbacks.size === 0) {
            listenersRef.current.delete(eventName);
          }
        }
        if (connection) {
          connection.off(eventName, callback);
        }
      };
    },
    [connection, connectionState],
  );

  // Invoke server method
  const invoke = useCallback(
    async (methodName, ...args) => {
      if (!connection || connectionState !== "Connected") {
        throw new Error("SignalR connection is not established");
      }
      return await connection.invoke(methodName, ...args);
    },
    [connection, connectionState],
  );

  // Auto-connect when token and storeId are available
  useEffect(() => {
    if (token && storeId) {
      startConnection();
    } else {
      stopConnection();
    }

    return () => {
      stopConnection();
    };
  }, [token, storeId, startConnection, stopConnection]); // Reconnect only when auth context changes

  const value = {
    connection,
    session: sessionRef.current,
    connectionState,
    error,
    isConnected: connectionState === "Connected",
    isConnecting: connectionState === "Connecting",
    isReconnecting: connectionState === "Reconnecting",
    isDisconnected: connectionState === "Disconnected",
    startConnection,
    stopConnection,
    on,
    invoke,
  };

  return (
    <SignalRContext.Provider value={value}>{children}</SignalRContext.Provider>
  );
};
